import { makeStyles } from "@material-ui/core";
import * as React from "react";
import { Colors } from "styles/Colors";
import { TrophyTypes } from "common/constants/TrophyTypes";
import TrophyService from "services/TrophyService";
import UserManager from "common/userManager";
import HtmlTooltip from "../HtmlTooltip/HtmlTooltip";
import GoldenTicketTooltip, {
	goldenTicketFlavor,
	trophyLabel,
} from "common/components/GoldenTicketTooltip/GoldenTicketTooltip";
import { EditOutlined } from "@material-ui/icons";
import ModalHelper from "common/helpers/modalHelper";
import TrophySelectorModal from "../Modals/TrophySelectorModal/TrophySelectorModal";
import { SetLoading } from "../Loader/Loader";
import UserProfileModalHelper from "common/helpers/userProfileModalHelper";

const useStyles = makeStyles((theme) => ({
	trophyList: {
		maxHeight: 500,
		overflowY: "auto",
		borderColor: Colors.body,
		display: "flex",
		flexWrap: "wrap",
		justifyContent: "center",
		margin: "-6px -12px -12px",
	},
	awardContainer: {
		width: 116,
		margin: "8px 7px",
		flexShrink: 0,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		borderTopLeftRadius: 12,
		borderTopRightRadius: 12,
		position: "relative",
	},
	awardImage: {
		maxHeight: 90,
		maxWidth: 90,
		margin: 10,
	},
	award: {
		display: "flex",
		justifyContent: "center",
		maxWidth: 100,
	},
	awardImageArea: {
		height: 110,
		width: "100%",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	eventName: {
		height: 36,
		display: "flex",
		justifyContent: "center",
		textAlign: "center",
		fontSize: 12,
		padding: "0 8px",
	},
	eventNameText: {
		display: "-webkit-box",
		"-webkit-line-clamp": 2,
		"-webkit-box-orient": "vertical",
		overflow: "hidden",
		lineHeight: 1.4,
	},
	patreonLevel: {
		height: 146,
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		textAlign: "center",
	},
	"@keyframes shimmer": {
		"0%": { backgroundPosition: "200% 0" },
		"100%": { backgroundPosition: "-200% 0" },
	},
	shimmer: {
		background: `linear-gradient(90deg, ${Colors.panel} 25%, ${Colors.panelLight} 50%, ${Colors.panel} 75%)`,
		backgroundSize: "400% 100%",
		animation: "$shimmer 2.4s ease infinite",
		borderRadius: 8,
	},
	shimmerImage: {
		height: 90,
		width: "calc(100% - 20px)",
		margin: 10,
	},
	shimmerText: {
		display: "inline-block",
		height: 12,
		width: 80,
		marginTop: 6,
	},
	editIcon: {
		position: "absolute",
		right: 8,
		top: 8,
		height: 20,
		width: 20,
		stroke: theme.palette.secondary.main,
		fill: theme.palette.secondary.main,
		zIndex: 1,
		"&:hover": {
			cursor: "pointer",
		},
	},
	editIconInner: {
		height: 20,
		width: 20,
		stroke: theme.palette.secondary.main,
		fill: theme.palette.secondary.main,
	},
}));

const trophyGradientColors = {
	[TrophyTypes.Wood]: Colors.wood,
	[TrophyTypes.Bronze]: Colors.bronze,
	[TrophyTypes.Silver]: Colors.silver,
	[TrophyTypes.Gold]: Colors.gold,
	[TrophyTypes.Platinum]: Colors.platinum,
	[TrophyTypes.Diamond]: Colors.diamond,
	[TrophyTypes.GoldenTicket]: Colors.goldenTicket,
};

export function getTrophyGradientBackground(trophyTypeId) {
	const color = trophyGradientColors[trophyTypeId] ?? Colors.panelLight;
	return `linear-gradient(217deg, ${color} 0%, transparent 60%)`;
}

export default function TrophyCase(props) {
	const { userId, awards, slots } = props;
	const isCurrentUser = userId === UserManager.getUserId();
	const [trophyCase, setTrophyCase] = React.useState(null);
	const [loadedImages, setLoadedImages] = React.useState({});

	React.useEffect(() => {
		setTrophyCase(null);
		setLoadedImages({});
		TrophyService.getUserTrophyCase(userId).then((awardData) => {
			setTrophyCase(awardData);
		});
	}, [userId]);

	const styles = useStyles();

	const selectTrophyCaseSlot = (slotId, trophyId, eventId) => {
		ModalHelper.closeAllModals();
		SetLoading(true, "Updating Trophy Case");
		TrophyService.updateTrophyCase(slotId, trophyId, eventId).then(() => {
			SetLoading(false);
			UserProfileModalHelper.openProfile(props.userId);
		});
	};

	const editTrophySlot = (slotId) => {
		const except = trophyCase.filter(
			(trophyCase) => trophyCase.slot !== slotId
		);
		ModalHelper.openModal(<TrophySelectorModal />, () => {}, {
			header: "Select a trophy to pin",
			awards,
			except,
			onSelect: selectTrophyCaseSlot.bind(this, slotId),
		});
	};

	return (
		<div className={styles.trophyList}>
			{Array.from({ length: slots }, (x, i) => i).map((index) => {
				const record = trophyCase?.find((tc) => tc.slot === index);
				const hasSlotAccess = slots >= index + 1;
				const isOpenSlot = !!trophyCase && !record;
				const ready = !!record && !!loadedImages[index];
				const markLoaded = () => {
					setLoadedImages((prev) => ({ ...prev, [index]: true }));
				};
				const editClicked = () => {
					editTrophySlot(index);
				};
				return (
					<div
						key={index}
						className={styles.awardContainer}
						style={{
							background: getTrophyGradientBackground(record?.trophyTypeId),
						}}
					>
						{!!trophyCase && isCurrentUser && hasSlotAccess && (
							<HtmlTooltip title={<>Edit</>}>
								<span className={styles.editIcon} onClick={editClicked}>
									<EditOutlined className={styles.editIconInner} />
								</span>
							</HtmlTooltip>
						)}
						{!isOpenSlot && (
							<>
								<div className={styles.awardImageArea}>
									{record && (
										<GoldenTicketTooltip record={record}>
											<div
												className={styles.award}
												title={
													goldenTicketFlavor(record)
														? undefined
														: record.eventName
												}
												style={ready ? undefined : { display: "none" }}
											>
												<img
													className={styles.awardImage}
													alt=""
													src={record.imagePath}
													onLoad={markLoaded}
													onError={markLoaded}
												/>
											</div>
										</GoldenTicketTooltip>
									)}
									{!ready && (
										<div
											className={`${styles.shimmer} ${styles.shimmerImage}`}
										/>
									)}
								</div>
								<div className={styles.eventName}>
									{ready ? (
										<span className={styles.eventNameText}>
											{trophyLabel(record)}
										</span>
									) : (
										<span
											className={`${styles.shimmer} ${styles.shimmerText}`}
										/>
									)}
								</div>
							</>
						)}
						{isOpenSlot && (
							<div className={styles.patreonLevel}>
								<span>
									<span>Open</span>
								</span>
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
}
